import {
  DELETE_DRAFT_FAILURE,
  DELETE_DRAFT_REQUEST,
  DELETE_DRAFT_SUCCESS,
} from 'drafts/containers/draft-constants';
import { combineReducers } from 'redux';
import { combineActions, handleActions } from 'redux-actions';
import { types } from 'utils/mappings';
import { CLEAR_NAVIGATION_DETAILS } from '../../../common/navigation/containers/constants';
import {
  DISCARD_LOCK_FAILURE,
  DISCARD_LOCK_REQUEST,
  DISCARD_LOCK_SUCCESS,
  UNLOCK_LOCK_REQUEST,
  UNLOCK_LOCK_SUCCESS,
} from '../../../common/publish-steps/containers/publish-steps-constants';
import { SAVE_WIP_CODES_ORDER_SUCCESS } from '../../../settings/containers/wip-codes/constants';
import { SAVE_PARTNER_SUCCESS } from '../../change-partner/containers/change-partner-constants';
import { CREATE_ORDER_SUCCESS } from '../../create/containers/constants';
import {
  GET_LOGISTICS_DATES_SUCCESS,
  UPDATE_CONSOLIDATION, UPDATE_URGENCY,
} from '../../parts/containers/parts-constants';
import { GET_TASKLIST_SUCCESS } from '../../task-list/containers/task-list-constants';
import * as actionTypes from './details-constants';

export const data = handleActions(
  {
    [actionTypes.GET_ORDER_DETAILS_REQUEST]: (state, { payload }) =>
      payload?.updateOrderDetailsStatus || payload?.skipLoading ? state : {},
    [combineActions(
      actionTypes.GET_ORDER_DETAILS_SUCCESS,
      actionTypes.SAVE_ORDER_SUCCESS,
      SAVE_PARTNER_SUCCESS,
    )]: (state, { payload }) => payload.details.order,
  },
  {},
);

export const resetDirtyIndicator = handleActions(
  {
    [combineActions(
      'LOCATION_CHANGE',
      DISCARD_LOCK_FAILURE,
      DISCARD_LOCK_REQUEST,
      UNLOCK_LOCK_REQUEST,
      UNLOCK_LOCK_REQUEST,
    )]: () => false,
    [combineActions(
      DISCARD_LOCK_SUCCESS,
      UNLOCK_LOCK_SUCCESS,
    )]: () => true,
  },
  false,
);

export const warnings = handleActions(
  {
    [combineActions(
      'LOCATION_CHANGE',
      actionTypes.SAVE_ORDER_REQUEST,
    )]: () => [],
    [actionTypes.GET_ORDER_DETAILS_REQUEST]: (state, { payload }) =>
      payload?.skipLoading ? state : [],
    [combineActions(actionTypes.SAVE_ORDER_SUCCESS)]: (
      state,
      { payload },
    ) => payload.warnings,
  },
  [],
);

export const lock = handleActions(
  {
    [actionTypes.GET_ORDER_DETAILS_REQUEST]: (state, { payload }) =>
      payload?.skipLoading ? state : null,
    [combineActions(actionTypes.GET_ORDER_DETAILS_SUCCESS, actionTypes.SAVE_ORDER_SUCCESS)]: (
      state,
      { payload },
    ) => payload.lock,
  },
  null,
);

export const lockingErrorType = handleActions(
  {
    [actionTypes.GET_ORDER_DETAILS_REQUEST]: (state, { payload }) =>
      payload?.skipLoading ? state : null,
    [combineActions(actionTypes.GET_ORDER_DETAILS_SUCCESS, actionTypes.SAVE_ORDER_SUCCESS)]: (
      state,
      { payload },
    ) => payload.lockingErrorType,
  },
  null,
);

export const partners = handleActions(
  {
    [actionTypes.GET_ORDER_DETAILS_REQUEST]: (state, { payload }) =>
      payload?.skipLoading ? state : [],
    [combineActions(
      actionTypes.GET_ORDER_DETAILS_SUCCESS,
      actionTypes.SAVE_ORDER_SUCCESS,
      SAVE_PARTNER_SUCCESS,
    )]: (state, { payload }) => {
      const linkedPartners = payload.details.order.partners;
      if (!linkedPartners || !payload.partners) {
        return state;
      }
      return payload.partners?.filter(
        p =>
          linkedPartners.findIndex(
            lp =>
              lp.number === p.number &&
              lp?.function?.toLowerCase() === p?.partnerFunction?.toLowerCase(),
          ) > -1,
      );
    },
  },
  [],
);

export const userStatuses = handleActions(
  {
    [actionTypes.GET_ORDER_DETAILS_REQUEST]: (state, { payload }) =>
      payload?.skipLoading ? state : [],
    [combineActions(
      actionTypes.GET_ORDER_DETAILS_SUCCESS,
      actionTypes.SAVE_ORDER_SUCCESS,
    )]: (state, { payload }) => payload.userStatuses,
  },
  [],
);

export const orderPartList = handleActions(
  {
    [actionTypes.GET_ORDER_DETAILS_REQUEST]: (state, { payload }) =>
      payload?.skipLoading ? state : {},
    [combineActions(actionTypes.GET_ORDER_DETAILS_SUCCESS, actionTypes.SAVE_ORDER_SUCCESS)]: (
      state,
      { payload },
    ) => state?.parts?.length ? {
      ...payload.orderPartList,
      parts: payload.orderPartList?.parts?.map((p) => {
        const existingPart = state?.parts?.find(sp => sp.id === p.id);
        if (existingPart) {
          return {
            ...p,
            deliveryDate: existingPart.deliveryDate,
            logisticsDate: existingPart.logisticsDate,
            logisticsDetails: existingPart.logisticsDetails,
            shippingDate: existingPart.shippingDate,
            partId: existingPart.partId,
          };
        }
        return p;
      }),
    } : payload.orderPartList,
    [GET_LOGISTICS_DATES_SUCCESS]: (state, { payload }) => ({
      ...state,
      parts: state.parts?.map((p) => {
        const logisticsDateOrder = payload.partsLogisticsDetails.find(l => l.partId === p.id);
        if (logisticsDateOrder) {
          return {
            ...logisticsDateOrder,
            ...p,
          };
        }
        return p;
      }),
    }),
    [SAVE_PARTNER_SUCCESS]: (state, { payload }) => {
      if (payload.isShipTo) {
        // we'll make sure the componentWillReceiveProps will trigger in our component,
        // by destructuring the payload.
        // This way, we can detect that the shipto changed & we need to edit partlist headers.
        return { ...payload.partList, parts: state.parts || [] };
      }
      return state;
    },
    [GET_TASKLIST_SUCCESS]: (state, { payload }) => ({
      ...state,
      parts: payload.orderPartList.parts,
      urgency: payload.orderPartList.urgency,
      partialShipment: payload.orderPartList.partialShipment,
    }),
    [UPDATE_CONSOLIDATION]: (state, { payload }) => ({
      ...state,
      parts: payload.parts,
      partialShipment: payload.value,
    }),
    [UPDATE_URGENCY]: (state, { payload }) => ({
      ...state,
      parts: payload.parts,
      urgency: payload.value,
    }),
  },
  {},
);

export const subcontracts = handleActions(
  {
    [actionTypes.GET_ORDER_DETAILS_REQUEST]: (state, { payload }) =>
      payload?.skipLoading ? state : [],
    [combineActions(actionTypes.GET_ORDER_DETAILS_SUCCESS, actionTypes.SAVE_ORDER_SUCCESS)]: (
      state,
      { payload },
    ) => payload.subContracts,
  },
  [],
);

export const linkedOrders = handleActions(
  {
    [actionTypes.GET_ORDER_DETAILS_REQUEST]: (state, { payload }) =>
      payload?.skipLoading ? state : [],
    [combineActions(actionTypes.GET_ORDER_DETAILS_SUCCESS, actionTypes.SAVE_ORDER_SUCCESS)]: (
      state,
      { payload },
    ) => payload.linkedOrders,
  },
  [],
);

export const resetFields = handleActions(
  {
    [combineActions(
      'LOCATION_CHANGE',
      actionTypes.GET_ORDER_DETAILS_FAILURE,
      actionTypes.GET_ORDER_DETAILS_REQUEST,
    )]: () => false,
    [actionTypes.GET_ORDER_DETAILS_SUCCESS]: (
      state,
      { payload },
    ) => payload.resetFields || false,
  },
  false,
);

export const wipCodesChanged = handleActions(
  {
    [combineActions(
      'LOCATION_CHANGE',
      actionTypes.SAVE_ORDER_REQUEST,
      actionTypes.GET_ORDER_DETAILS_SUCCESS,
      actionTypes.RESET_WIPCODES_CHANGED,
    )]: () => false,
    [SAVE_WIP_CODES_ORDER_SUCCESS]: () => true,
  },
  false,
);

export const validationInfo = handleActions(
  {
    [actionTypes.GET_ORDER_DETAILS_REQUEST]: (state, { payload }) =>
      payload?.skipLoading ? state : [],
    [combineActions(
      actionTypes.GET_ORDER_DETAILS_SUCCESS,
      actionTypes.SAVE_ORDER_SUCCESS,
      actionTypes.GET_ORDER_DETAILS_STATUS_SUCCES,
    )]: (
      state,
      { payload },
    ) => payload?.validationInfo || [],
  },
  [],
);

export const linkedPlanning = handleActions(
  {
    [actionTypes.GET_ORDER_DETAILS_REQUEST]: (state, { payload }) =>
      payload?.skipLoading ? state : [],
    [combineActions(
      actionTypes.GET_ORDER_DETAILS_SUCCESS,
      actionTypes.SAVE_ORDER_SUCCESS,
      actionTypes.GET_ORDER_DETAILS_STATUS_SUCCES,
    )]: (
      state,
      { payload },
    ) => payload.linkedPlanning,
    [actionTypes.GET_LINKED_PLANNINGS_SUCCESS]: (state, { payload }) => payload,
    [combineActions(
      actionTypes.GET_LINKED_PLANNINGS_REQUEST,
      actionTypes.GET_LINKED_PLANNINGS_FAILURE,
    )]: state => state || [],
  },
  [],
);


export const isLoading = handleActions(
  {
    [actionTypes.GET_ORDER_DETAILS_REQUEST]: (state, { payload }) => !payload?.skipLoading,
    [combineActions(
      'LOCATION_CHANGE',
      actionTypes.GET_ORDER_DETAILS_SUCCESS,
      actionTypes.GET_ORDER_DETAILS_FAILURE,
    )]: () => false,
  },
  false,
);

export const error = handleActions(
  {
    [combineActions(
      'LOCATION_CHANGE',
      actionTypes.GET_ORDER_DETAILS_SUCCESS,
      actionTypes.SAVE_ORDER_REQUEST,
      actionTypes.SAVE_ORDER_SUCCESS,
      DELETE_DRAFT_SUCCESS,
      actionTypes.PLANNING_UPDATE_ORDER_SOCKET_EVENTS_SUCCESS,
      actionTypes.PLANNING_UPDATE_ORDER_SOCKET_EVENTS,
    )]: () => ({}),
    [actionTypes.GET_ORDER_DETAILS_REQUEST]: (state, { payload }) =>
      payload?.skipLoading ? state : {},
    [combineActions(
      actionTypes.SAVE_ORDER_FAILURE,
      actionTypes.GET_ORDER_DETAILS_FAILURE,
      actionTypes.ADD_SUBORDER_FAILURE,
      DELETE_DRAFT_FAILURE,
      actionTypes.PLANNING_UPDATE_ORDER_SOCKET_EVENTS_FAILURE,
    )]: (state, { payload }) => payload.error,
  },
  {},
);

export const isSaving = handleActions(
  {
    [actionTypes.SAVE_ORDER_REQUEST]: () => true,
    [combineActions(
      actionTypes.SAVE_ORDER_SUCCESS,
      actionTypes.SAVE_ORDER_FAILURE,
    )]: () => false,
    [actionTypes.GET_ORDER_DETAILS_REQUEST]: (state, { payload }) =>
      payload?.skipLoading ? state : false,
    [actionTypes.GET_ORDER_DETAILS_SUCCESS]: () => false,
  },
  false,
);

export const saveSuccess = handleActions(
  {
    [actionTypes.SAVE_ORDER_SUCCESS]: () => true,
    [combineActions(actionTypes.SAVE_ORDER_REQUEST, actionTypes.SAVE_ORDER_FAILURE)]: () => false,
  },
  false,
);

export const removeSuccess = handleActions(
  {
    [DELETE_DRAFT_SUCCESS]: () => true,
    [combineActions(
      'LOCATION_CHANGE',
      DELETE_DRAFT_REQUEST,
      DELETE_DRAFT_FAILURE,
      actionTypes.GET_ORDER_DETAILS_REQUEST,
      CLEAR_NAVIGATION_DETAILS,
    )]: () => false,
  },
  false,
);

export const orderStates = handleActions(
  {
    [actionTypes.GET_ORDER_DETAILS_REQUEST]: (state, { payload }) =>
      payload?.skipLoading ? state : [],
    [combineActions(actionTypes.GET_ORDER_DETAILS_SUCCESS, actionTypes.SAVE_ORDER_SUCCESS)]: (
      state,
      { payload },
    ) => payload.orderStatusModels,
  },
  [],
);

export const changes = handleActions(
  {
    LOCATION_CHANGE: () => ({}),
    [actionTypes.GET_ORDER_DETAILS_REQUEST]: (state, { payload }) =>
      payload?.skipLoading ? state : {},
    [combineActions(actionTypes.GET_ORDER_DETAILS_SUCCESS, actionTypes.SAVE_ORDER_SUCCESS)]: (
      state,
      { payload },
    ) => payload.changes,
  },
  {},
);

export const notes = handleActions(
  {
    [actionTypes.GET_ORDER_DETAILS_REQUEST]: (state, { payload }) =>
      payload?.skipLoading ? state : [],
    [combineActions(actionTypes.GET_ORDER_DETAILS_SUCCESS, actionTypes.SAVE_ORDER_SUCCESS)]: (
      state,
      { payload },
    ) => payload.notes,
  },
  [],
);

export const serviceNoteValue = handleActions(
  {
    [actionTypes.GET_ORDER_DETAILS_REQUEST]: (state, { payload }) =>
      payload?.skipLoading ? state : '',
    [combineActions(actionTypes.GET_ORDER_DETAILS_SUCCESS, actionTypes.SAVE_ORDER_SUCCESS)]: (
      state,
      { payload },
    ) => {
      if (payload.notes?.find(n => n.noteType === types.order.serviceOrderNoteType)) {
        return payload.notes
          ?.find(n => n.noteType === types.order.serviceOrderNoteType)
          .noteTexts?.filter(n => n.textLine !== null)
          .reduce((acc, curr) => `${acc}${acc ? '\n' : ''}${curr.textLine}`, '');
      }
      return '';
    },
  },
  '',
);

export const invoiceNoteValue = handleActions(
  {
    [actionTypes.GET_ORDER_DETAILS_REQUEST]: (state, { payload }) =>
      payload?.skipLoading ? state : '',
    [combineActions(actionTypes.GET_ORDER_DETAILS_SUCCESS, actionTypes.SAVE_ORDER_SUCCESS)]: (
      state,
      { payload },
    ) => {
      if (payload.notes?.find(n => n.noteType === types.order.invoiceTextNoteType)) {
        return payload.notes
          ?.find(n => n.noteType === types.order.invoiceTextNoteType)
          .noteTexts?.filter(n => n.textLine !== null)
          .reduce((acc, curr) => `${acc}${acc ? '\n' : ''}${curr.textLine}`, '');
      }
      return '';
    },
  },
  '',
);

export const redirect = handleActions(
  {
    [combineActions(
      actionTypes.GET_ORDER_DETAILS_REQUEST,
      actionTypes.ADD_SUBORDER_REQUEST,
      actionTypes.ADD_SUBORDER_FAILURE,
    )]: () => ({}),
    [actionTypes.ADD_SUBORDER_SUCCESS]: (state, action) => ({
      url: action.payload.redirectUri,
      key: 'order.fieldEngineer.ADD_SUCCESS',
    }),
    [CREATE_ORDER_SUCCESS]: (state, action) => ({
      url: action.payload.redirectUri,
      key: action.payload.assignedToAction ?
        'order.LINK_SUCCESS' :
        'order.fieldEngineer.ADD_SUCCESS',
    }),
  },
  {},
);

export const lockingErrors = handleActions(
  {
    [actionTypes.GET_ORDER_DETAILS_REQUEST]: (state, { payload }) =>
      payload?.skipLoading ? state : [],
    [combineActions(actionTypes.GET_ORDER_DETAILS_SUCCESS, actionTypes.SAVE_ORDER_SUCCESS)]: (
      state,
      { payload },
    ) => payload.lockingErrors || [],
  },
  [],
);

export const isExternalRemove = handleActions(
  {
    LOCATION_CHANGE: () => false,
    [actionTypes.EXTERNAL_REMOVE]: (state, { payload }) => payload,
  },
  false,
);

export default combineReducers({
  data,
  isLoading,
  error,
  lock,
  lockingErrorType,
  isSaving,
  saveSuccess,
  removeSuccess,
  orderStates,
  changes,
  notes,
  serviceNoteValue,
  invoiceNoteValue,
  partners,
  userStatuses,
  orderPartList,
  subcontracts,
  linkedOrders,
  redirect,
  lockingErrors,
  linkedPlanning,
  warnings,
  resetDirtyIndicator,
  validationInfo,
  isExternalRemove,
  resetFields,
  wipCodesChanged,
});
